import React, { FC, ReactNode, useMemo } from 'react';
import dynamic from 'next/dynamic';

import ItemPageMetadata from '~/app/components/ItemPage/components/ItemPageMetadata';
import { PageSectionTypes } from '~/app/components/ItemPage/sections/types';
import { CORE_SECTION_COMPONENTS } from '~/app/components/ItemPage/constants';
import ItemPageHeader from '~/app/components/ItemPage/components/ItemPageHeader';
import ItemPage, { ItemPageProps } from '~/app/components/ItemPage';
import { PAGE_HEADER_HEIGHT } from '~/app/components/PageHeader';
import useSelectArtists from '~/app/lib/hooks/useSelectArtists';
import useSelectAlbum from '~/app/lib/hooks/useSelectAlbum';
import { PageTrackerContext } from '~/app/components/Page';
import LazyComponent from '~/app/components/LazyComponent';
import useHash from '~/app/lib/hooks/useHash';
import { useI18n } from '~/app/lib/i18n';
import { PageTypes } from '~/types';

import {
  PageSectionElement,
  PageSections,
} from '~/app/components/ItemPage/components/SectionRenderer';

import { AlbumPageActions } from './components/AlbumPageActions';
import { ALBUM_PAGE_SECTION_COMPONENTS } from './constants';
import useAlbumItemContext from './hooks/useAlbumItemContext';
import toStructuredData from './hooks/toStructuredData';
import useAlbumChecks from './hooks/useAlbumChecks';
import useAutoClaimAlbum from './hooks/useAutoClaimAlbum';

const sectionComponents = {
  ...CORE_SECTION_COMPONENTS,
  ...ALBUM_PAGE_SECTION_COMPONENTS,
};

const OrchardLegalFooterDynamic = dynamic(
  () => import('~/app/components/ItemPage/components/OrchardLegalFooter'),
  { ssr: true }
);

export interface ItemPageAlbumProps
  extends Pick<ItemPageProps, 'withRedirectIfNeeded'> {
  albumId: number;
  withAlbumChecks?: boolean;

  beforeHeader?: {
    height: string;
    content: ReactNode;
  };
}

const AlbumPage: FC<ItemPageAlbumProps> = ({
  albumId,
  withAlbumChecks = true,
  withRedirectIfNeeded = true,
  beforeHeader,
  ...itemPageProps
}) => {
  const { t } = useI18n();

  const album = useSelectAlbum(albumId)!;
  const itemContext = useAlbumItemContext({ album });

  const { showArtistIds } = itemContext.config;
  const artists = useSelectArtists(showArtistIds);
  const artistNames = artists?.map(({ name }) => name).join(', ');
  const { hasHashParam, backToBeforeFirstHash } = useHash();
  const editDetailsDialogOpen = hasHashParam('editDetails');

  if (withAlbumChecks) {
    useAlbumChecks(album);
  }

  useAutoClaimAlbum(itemContext);

  return (
    <ItemPage
      testId="albumPage"
      pageType={album.isPrerelease ? PageTypes.PRESAVE : PageTypes.ALBUM}
      key={albumId}
      isOwned={album.isOwned}
      isDraft={!!album.isDraft}
      pageBrand={album.pageBrand}
      pagePath={album.pagePath}
      itemContext={itemContext}
      pageOwnedByAccountIds={album.ownedByAccountIds}
      isLoading={!!album.isLoading}
      hasContent={!!album.name && !album.isShallow}
      withRedirectIfNeeded={withRedirectIfNeeded}
      error={album.error}
      trackerContext={useMemo(
        (): PageTrackerContext => ({
          albumId: album.id,
          albumName: album.name,
          albumPrerelease: album.isPrerelease,
          artistId: album.artistId,
          artistName: album.artistName,
        }),
        [album]
      )}
      header={useMemo(() => {
        return {
          height: `calc(${PAGE_HEADER_HEIGHT} + ${
            beforeHeader?.height ?? '0rem'
          })`,

          content: (
            <>
              {beforeHeader?.content}
              <ItemPageHeader
                pagePath={album.pagePath}
                userCanEdit={album.userCanEdit}
                renderActions={({ isOpen, actionButtonRef, onClose }) => {
                  return (
                    <AlbumPageActions
                      itemContext={itemContext}
                      isOpen={isOpen}
                      actionButtonRef={actionButtonRef}
                      onClose={onClose}
                    />
                  );
                }}
              />
            </>
          ),
        };
      }, [album])}
      content={useMemo(() => {
        return (
          <>
            <PageSections
              items={itemContext.layout.main}
              id=""
              components={sectionComponents}
              itemContext={itemContext}
              withTopMargin={false}
              groupSections={[
                [PageSectionTypes.PAGE_TITLE, PageSectionTypes.ICON_LINKS],
              ]}
            />
            <PageSectionElement>
              <OrchardLegalFooterDynamic />
            </PageSectionElement>
          </>
        );
      }, [album])}
      {...itemPageProps}
    >
      <ItemPageMetadata
        error={album.error}
        pagePath={album.pagePath}
        isOwned={album.isOwned!}
        itemContext={itemContext}
        defaultTitle={
          album.name &&
          t('item.albumPageMetaTitle', {
            albumName: album.name,
            artistNames: artistNames ?? '?',
          })
        }
        defaultDescription={
          album.name &&
          t('item.albumPageMetaDescription', {
            albumName: album.name,
            artistNames: artistNames ?? '?',
          })
        }
        // REVIEW: this could be a <StructuredData> component instead
        // of having to pass props down the tree to <PageMetaData>
        // which is getting quite overloaded now
        toStructuredData={(params: any) => {
          return toStructuredData({
            ...params,
            albumName: album.name,
            artistName: album.artistName,
            artistPagePath: album.artistPagePath,
            // TODO: add artist image (need resolving to full url)
            artistImageUrl: undefined,
          });
        }}
      />
      {editDetailsDialogOpen && (
        <LazyComponent
          // NOTE: webpack chunk name used to target script request in cypress
          loader={() =>
            import(
              /* webpackChunkName: "EditDetailsDialog" */ './components/EditDetailsDialog'
            )
          }
          onClose={backToBeforeFirstHash}
          album={album}
        />
      )}
    </ItemPage>
  );
};

export default AlbumPage;
